// components
import Button from '@components/Button/Button';
import { EmailSignatureFieldTypes } from '@components/EmailSignaturev2/consts';
import {
  EmailSignatureContactDetailsFieldTypes,
  type EmailSignatureIconName,
  type EmailSignatureSocialLink,
  EmailSignatureSocialLinkTypes,
  type EmailSignatureV2,
} from '@components/EmailSignaturev2/types';
import { faFacebook, faInstagram, faLinkedinIn } from '@fortawesome/free-brands-svg-icons';
import { faGlobe } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { analytics } from '@modules/analytics';
// utils
import {
  generateContactDetailsIconImage,
  generateNewEmailSignatureContactFieldId,
  generateNewEmailSignatureSocialLinkId,
  generateSocialLinkIconImage,
  getDefaultTitleForWebsiteIfNoUserInput,
  measureTextWidth,
} from '@modules/email-signatures-helpers';
// es-toolkit/compat: lodash filter iterates objects/Records (not in es-toolkit main)
import { filter } from 'es-toolkit/compat';
// hooks
import { useRouter } from 'next/router';
import { type FC, useCallback, useMemo, useState } from 'react';

import {
  MobileOnboardingInputsSection,
  MobileOnboardingInputsSectionHeader,
  MobileOnboardingInputsSectionHeaderContainer,
  MobileOnboardingNextButtonContainer,
} from './MobileOnboardingInputs';
import {
  GenericSocialFieldInputsImpl,
  SocialLinkFieldContainer,
  SocialLinkFieldContainerDivider,
  SocialLinkFieldContent,
  SocialLinkFieldEditSection,
  WebsiteFieldInputsPopupImpl,
} from './MobileSocialFieldInputs';
import styles from './OnboardingSocialLinksPageForMobile.module.scss';
import { type CommonOnboardingDetailsPageForMobileProps, MobileDetailsSteps } from './types';
import { deriveFieldWidth } from './utils';
import type { AnalyticsSignatureSocialLinkType } from '.cache/__types__';

function trackAddSocialLink(socialLinkType: string) {
  analytics.track({
    name: 'onboarding_signature_sociallink_added',
    properties: {
      social_link_type: socialLinkType as AnalyticsSignatureSocialLinkType,
    },
  });
}

function trackDeleteSocialLink(socialLinkType: string) {
  analytics.track({
    name: 'onboarding_signature_sociallink_deleted',
    properties: {
      social_link_type: socialLinkType as AnalyticsSignatureSocialLinkType,
    },
  });
}

function trackSaveSocialLink(socialLinkType: string) {
  analytics.track({
    name: 'onboarding_signature_sociallink_saved',
    properties: {
      social_link_type: socialLinkType as AnalyticsSignatureSocialLinkType,
    },
  });
}

type OnboardingSocialLinksPageForMobileProps = CommonOnboardingDetailsPageForMobileProps;

const OnboardingSocialLinksPageForMobile: FC<
  React.PropsWithChildren<OnboardingSocialLinksPageForMobileProps>
> = ({
  emailSignature,
  emailSignatureDispatch,
  handleSave,
  uploadEmailSignatureFieldImagesThunk,
  handleErrorUploadingImage,
}) => {
  const router = useRouter();
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const onboardingWebsite = useMemo(() => {
    const websites = filter(
      emailSignature.contactDetails,
      (emailSignatureContactDetailField) => emailSignatureContactDetailField.type === 'website',
    );

    // The onboarding website will always be the first one, because if someone signs up, add a new website
    // and comes back to this page, we will prompt them to create a new account or go to dashboard
    return websites.length > 0 ? websites[0] : null;
  }, [emailSignature.contactDetails]);

  const [openWebsiteInputs, setOpenWebsiteInputs] = useState(false);

  const [onboardingFacebook, onboardingLinkedin, onboardingInstagram] = useMemo(() => {
    const results: (EmailSignatureSocialLink | null)[] = [null, null, null];

    emailSignature.socialLinksOrder.forEach((socialLinkId) => {
      const socialLink = emailSignature.socialLinks[socialLinkId];

      if (socialLink.type === 'facebook') results[0] = socialLink;
      else if (socialLink.type === 'linkedin') results[1] = socialLink;
      else if (socialLink.type === 'instagram') results[2] = socialLink;
    });

    return results;
  }, [emailSignature.socialLinks, emailSignature.socialLinksOrder]);
  const [openFacebookInputs, setOpenFacebookInputs] = useState(false);
  const [openInstagramInputs, setOpenInstagramInputs] = useState(false);
  const [openLinkedinInputs, setOpenLinkedinInputs] = useState(false);

  const onWebsiteChange = useCallback(
    (updatedWebsite: string) => {
      trackSaveSocialLink('website');
      if (!onboardingWebsite) {
        const fieldIconImage = generateContactDetailsIconImage(
          'globe',
          emailSignature.design.themeColor,
        );
        if (!fieldIconImage) {
          return;
        }

        const newFieldId = generateNewEmailSignatureContactFieldId();
        uploadEmailSignatureFieldImagesThunk((emailSignatureImagesUploader) => {
          emailSignatureImagesUploader.uploadImage(
            newFieldId,
            fieldIconImage,
            EmailSignatureFieldTypes.CONTACT,
            handleErrorUploadingImage,
          );
        });

        const websiteTitle = getDefaultTitleForWebsiteIfNoUserInput(updatedWebsite);
        const fieldTextWidth = measureTextWidth(websiteTitle, emailSignature.design.font);
        const newWebsiteField = {
          baseIcon: 'globe' as EmailSignatureIconName,
          iconUrl: fieldIconImage,
          value: updatedWebsite,
          label: '',
          id: newFieldId,
          type: EmailSignatureContactDetailsFieldTypes.WEBSITE,
          fieldWidth: deriveFieldWidth(fieldTextWidth),
        };
        emailSignatureDispatch({
          type: 'ADD_CONTACT_DETAIL_FIELD',
          value: newWebsiteField,
        });
      } else {
        emailSignatureDispatch({
          type: 'UPDATE_CONTACT_DETAIL_FIELD_VALUE',
          value: { fieldId: onboardingWebsite.id, updatedFieldValue: updatedWebsite },
        });
      }
    },
    [
      onboardingWebsite,
      emailSignature.design.font,
      emailSignature.design.themeColor,
      emailSignatureDispatch,
      uploadEmailSignatureFieldImagesThunk,
      handleErrorUploadingImage,
    ],
  );

  const onWebsiteDelete = useCallback(() => {
    trackDeleteSocialLink('website');
    if (!onboardingWebsite) {
      return;
    } else {
      emailSignatureDispatch({
        type: 'DELETE_CONTACT_DETAIL_FIELD',
        value: onboardingWebsite.id,
      });
    }
  }, [emailSignatureDispatch, onboardingWebsite]);

  function onSocialLinkChange(
    fieldType: EmailSignatureSocialLinkTypes,
    updatedValue: string,
    currentField: EmailSignatureSocialLink | null,
  ) {
    trackSaveSocialLink(fieldType);
    if (!currentField) {
      const socialIconImage = generateSocialLinkIconImage(fieldType, emailSignature.design);
      if (!socialIconImage) {
        return;
      }

      const newSocialLinkId = generateNewEmailSignatureSocialLinkId();
      uploadEmailSignatureFieldImagesThunk((emailSignatureImagesUploader) => {
        emailSignatureImagesUploader.uploadImage(
          newSocialLinkId,
          socialIconImage,
          EmailSignatureFieldTypes.SOCIAL,
          handleErrorUploadingImage,
        );
      });

      emailSignatureDispatch({
        type: 'ADD_SOCIAL_LINK',
        value: {
          id: newSocialLinkId,
          type: fieldType,
          value: updatedValue,
          iconUrl: socialIconImage,
        },
      });
    } else {
      emailSignatureDispatch({
        type: 'UPDATE_SOCIAL_LINK_VALUE',
        value: { fieldId: currentField.id, updatedFieldValue: updatedValue },
      });
    }
  }

  function onSocialLinkDelete(currentField: EmailSignatureSocialLink | null) {
    if (!currentField) {
      return;
    } else {
      trackDeleteSocialLink(currentField.type);
      emailSignatureDispatch({
        type: 'DELETE_SOCIAL_LINK',
        value: currentField.id,
      });
    }
  }

  async function onClickNext() {
    setIsSaving(true);
    await handleSave(emailSignature, (savedEmailSignature: EmailSignatureV2) => {
      router.push(`/onboarding/email-signature/details/${MobileDetailsSteps.PREVIEW}`, undefined, {
        shallow: true,
      });
      emailSignatureDispatch({ type: 'IMPORT_EMAIL_SIGNATURE', value: savedEmailSignature });
      setIsSaving(false);
    });
  }

  return (
    <MobileOnboardingInputsSection>
      <MobileOnboardingInputsSectionHeaderContainer>
        <MobileOnboardingInputsSectionHeader>
          Let&apos;s add your social links.
        </MobileOnboardingInputsSectionHeader>
      </MobileOnboardingInputsSectionHeaderContainer>

      <div className={styles['social-inputs-group']}>
        <SocialLinkFieldContainer>
          <div className={styles['web-icon']}>
            <FontAwesomeIcon icon={faGlobe} />
          </div>
          <SocialLinkFieldContent
            title="Website"
            value={onboardingWebsite?.value || ''}
            linkType="website"
          />

          <SocialLinkFieldEditSection
            mode={onboardingWebsite ? 'edit' : 'add'}
            onEditButtonClick={() => {
              trackAddSocialLink('website');
              setOpenWebsiteInputs(true);
            }}
          />
        </SocialLinkFieldContainer>

        <SocialLinkFieldContainerDivider />

        <SocialLinkFieldContainer>
          <div className={styles['linkedin-icon']}>
            <FontAwesomeIcon icon={faLinkedinIn} />
          </div>
          <SocialLinkFieldContent
            title="Linkedin"
            value={onboardingLinkedin?.value || ''}
            linkType={EmailSignatureSocialLinkTypes.LINKEDIN}
          />

          <SocialLinkFieldEditSection
            mode={onboardingLinkedin ? 'edit' : 'add'}
            onEditButtonClick={() => {
              trackAddSocialLink(EmailSignatureSocialLinkTypes.LINKEDIN);
              setOpenLinkedinInputs(true);
            }}
          />
        </SocialLinkFieldContainer>

        <SocialLinkFieldContainerDivider />

        <SocialLinkFieldContainer>
          <div className={styles['instagram-icon']}>
            <FontAwesomeIcon icon={faInstagram} />
          </div>
          <SocialLinkFieldContent
            linkType={EmailSignatureSocialLinkTypes.INSTAGRAM}
            title="Instagram"
            value={onboardingInstagram?.value || ''}
          />

          <SocialLinkFieldEditSection
            mode={onboardingInstagram ? 'edit' : 'add'}
            onEditButtonClick={() => {
              trackAddSocialLink(EmailSignatureSocialLinkTypes.INSTAGRAM);
              setOpenInstagramInputs(true);
            }}
          />
        </SocialLinkFieldContainer>

        <SocialLinkFieldContainerDivider />

        <SocialLinkFieldContainer>
          <div className={styles['facebook-icon']}>
            <FontAwesomeIcon icon={faFacebook} />
          </div>
          <SocialLinkFieldContent
            linkType={EmailSignatureSocialLinkTypes.FACEBOOK}
            title="Facebook"
            value={onboardingFacebook?.value || ''}
          />

          <SocialLinkFieldEditSection
            mode={onboardingFacebook ? 'edit' : 'add'}
            onEditButtonClick={() => {
              trackAddSocialLink(EmailSignatureSocialLinkTypes.FACEBOOK);
              setOpenFacebookInputs(true);
            }}
          />
        </SocialLinkFieldContainer>

        <WebsiteFieldInputsPopupImpl
          currentValue={onboardingWebsite?.value || ''}
          onSaveClick={onWebsiteChange}
          onDeleteButtonClick={onWebsiteDelete}
          show={openWebsiteInputs}
          title={'Website'}
          onCloseClick={() => setOpenWebsiteInputs(false)}
        />

        <GenericSocialFieldInputsImpl
          currentValue={onboardingFacebook?.value || ''}
          show={openFacebookInputs}
          title={'Facebook'}
          onCloseClick={() => setOpenFacebookInputs(false)}
          onSaveClick={(updatedFacebookValue) =>
            onSocialLinkChange(
              EmailSignatureSocialLinkTypes.FACEBOOK,
              updatedFacebookValue,
              onboardingFacebook,
            )
          }
          onDeleteButtonClick={() => onSocialLinkDelete(onboardingFacebook)}
        />

        <GenericSocialFieldInputsImpl
          currentValue={onboardingInstagram?.value || ''}
          show={openInstagramInputs}
          title={'Instagram'}
          onCloseClick={() => setOpenInstagramInputs(false)}
          onSaveClick={(updatedInstagramValue) =>
            onSocialLinkChange(
              EmailSignatureSocialLinkTypes.INSTAGRAM,
              updatedInstagramValue,
              onboardingInstagram,
            )
          }
          onDeleteButtonClick={() => onSocialLinkDelete(onboardingInstagram)}
        />

        <GenericSocialFieldInputsImpl
          currentValue={onboardingLinkedin?.value || ''}
          show={openLinkedinInputs}
          title={'Linkedin'}
          onCloseClick={() => setOpenLinkedinInputs(false)}
          onSaveClick={(updatedLinkedinValue) =>
            onSocialLinkChange(
              EmailSignatureSocialLinkTypes.LINKEDIN,
              updatedLinkedinValue,
              onboardingLinkedin,
            )
          }
          onDeleteButtonClick={() => onSocialLinkDelete(onboardingLinkedin)}
        />
      </div>
      <MobileOnboardingNextButtonContainer>
        <Button size="large" onClick={onClickNext} loading={isSaving}>
          Next
        </Button>
      </MobileOnboardingNextButtonContainer>
    </MobileOnboardingInputsSection>
  );
};

export default OnboardingSocialLinksPageForMobile;
